/**
 * LocationsData.js
 * Contiene datos de localidades y sus códigos postales
 * Incluye funciones para obtener barrios desde la API
 */

import { BACKEND_URL } from '../../Config/Const';

// Datos de localidades con sus códigos postales
export const localidades = [
  { nombre: 'RAFAELA', codPostal: 2300 },
  { nombre: 'SUSANA', codPostal: 2301 },
  { nombre: 'BELLA ITALIA', codPostal: 2301.9 },
  { nombre: 'PTE. ROCA', codPostal: 2301.6 },
  { nombre: 'SAN FRANCISCO', codPostal: 2400 },
  { nombre: 'FRONTERA', codPostal: 2438 }
];

// Función para obtener el código postal a partir del nombre de la localidad
export const obtenerCodigoPostal = (nombreLocalidad) => {
  const localidad = localidades.find(loc => loc.nombre === nombreLocalidad);
  return localidad ? localidad.codPostal : null;
};

// Función para obtener el nombre de localidad a partir del código postal
export const obtenerNombreLocalidad = (codigoPostal) => {
  const localidad = localidades.find(loc => loc.codPostal === codigoPostal);
  return localidad ? localidad.nombre : null;
};

// Función para obtener las opciones para el select
export const obtenerOpcionesLocalidades = (filtrarPorCodigoPostal = false) => {
  // Si se solicita filtrar y estamos en entorno web
  if (filtrarPorCodigoPostal && typeof window !== 'undefined') {
    const codigoPostalGuardado = localStorage.getItem('codigoPostal');
    
    // Si hay un código postal guardado, filtrar por él
    if (codigoPostalGuardado) {
      const localidadesFiltradas = localidades.filter(loc => 
        loc.codPostal.toString() === codigoPostalGuardado.toString()
      );
      
      // Si encontramos localidades que coinciden, devolver solo esas
      if (localidadesFiltradas.length > 0) {
        return localidadesFiltradas.map(loc => ({
          label: loc.nombre,
          value: loc.nombre
        }));
      }
    }
  }
  
  // Si no se filtra o no se encontraron coincidencias, devolver todas las localidades
  return localidades.map(loc => ({
    label: loc.nombre,
    value: loc.nombre
  }));
};

// Función para obtener localidades filtradas por código postal
export const obtenerLocalidadesPorCodigoPostal = (codigoPostal = null) => {
  // Si no se proporciona un código postal y estamos en entorno web, intentar obtenerlo del localStorage
  if (!codigoPostal && typeof window !== 'undefined') {
    codigoPostal = localStorage.getItem('codigoPostal');
  }
  
  // Si tenemos un código postal, filtrar las localidades
  if (codigoPostal) {
    return localidades.filter(loc => loc.codPostal.toString() === codigoPostal.toString());
  }
  
  // Si no hay código postal disponible, devolver todas las localidades
  return localidades;
};

/**
 * Función para obtener los barrios según el código postal desde la API
 * @param {number} [codPostal] - Código postal de la localidad (opcional, si no se pasa se obtiene del localStorage)
 * @returns {Promise<Array>} - Lista de barrios
 */
export const obtenerBarriosPorCodigoPostal = async (codPostal) => {
  try {
    // Si no se proporciona el código postal, intentar obtenerlo del localStorage
    if (!codPostal && typeof window !== 'undefined') {
      codPostal = localStorage.getItem('codigoPostal');
    }
    
    // Si no hay código postal disponible, devolver un array vacío
    if (!codPostal) {
      return [];
    }
    
    // Crear objeto de parámetros
    const queryParams = new URLSearchParams();
    queryParams.append('codPostal', codPostal);
    
    // Realizar la llamada a la API
    const response = await fetch(`${BACKEND_URL}/rest/usuarios/barrios?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error al obtener barrios: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Verificar la estructura de la respuesta y devolver los barrios
    if (data && data.dsResultado && Array.isArray(data.dsResultado.barrios)) {
      return data.dsResultado.barrios.map(barrio => ({
        codigo: barrio.codBarrio,
        nombre: barrio.barrio
      }));
    }
    
    return [];
  } catch (error) {
    // Silenciosamente manejar el error y devolver un array vacío
    return [];
  }
};

export default {
  localidades,
  obtenerCodigoPostal,
  obtenerNombreLocalidad,
  obtenerOpcionesLocalidades,
  obtenerBarriosPorCodigoPostal
};
